/**
 * Smart catalogue search — query parsing, era synonyms, and in-memory matching.
 *
 * Pure. No network, no React, no theme. This file is the brain; the network
 * lives in cardData and the pixels live in SmartSearchResults, so the same
 * behaviour can be mounted on the Explore-tab menu, the scanner's manual
 * search, and later the All tab, the wish list and Trade Now without a second
 * implementation drifting away from this one.
 *
 * What "smart" has to mean here, in the owner's words: "char 004" is Charizard
 * number 4, a bare number is a number across every set, "evolv" is a set, and
 * "wizards of the coast" is an era whose id is four letters that appear nowhere
 * in that phrase. Token order never matters and zero padding never matters.
 */

// ---------------------------------------------------------------------------
// ERA SYNONYMS
//
// The 13 era ids are short codes, so no amount of string matching gets from
// "wizards of the coast" to `wotc`. This table is the whole answer. To teach
// the search a new phrase, add it to the right row — nothing below this table
// needs to change, and nothing outside this file does either.
//
// Phrases are matched loosely: punctuation and the words "and"/"&" are dropped
// on both sides first, so "sword & shield" and "sword shield" are one entry.
// ---------------------------------------------------------------------------
export const ERA_SYNONYMS = {
  wotc: [
    'wizards of the coast', 'wizards', 'wotc', 'base set era', 'base era',
    'vintage', 'classic', 'original', 'old school', 'nineties', '1999',
  ],
  'e-reader': ['e reader', 'ereader', 'e-reader', 'e card', 'ecard'],
  ex: ['ex era', 'ex', 'ruby sapphire', 'ruby and sapphire'],
  dp: ['diamond pearl', 'diamond and pearl', 'dp', 'dia pearl'],
  platinum: ['platinum', 'plat', 'pt'],
  hgss: ['heartgold soulsilver', 'heart gold soul silver', 'hgss', 'hgs', 'heartgold', 'soulsilver'],
  bw: ['black white', 'black and white', 'bw', 'bandw'],
  xy: ['xy', 'x y', 'x and y'],
  sm: ['sun moon', 'sun and moon', 'sm'],
  swsh: ['sword shield', 'sword and shield', 'swsh', 'sws'],
  sv: ['scarlet violet', 'scarlet and violet', 'sv', 'scarlet', 'violet'],
  me: ['mega evolution', 'mega', 'me era'],
  tcgp: ['tcg pocket', 'pocket', 'tcgp', 'digital', 'pokemon tcg pocket'],
};

/**
 * Words that carry no signal and actively hurt a card-name query. "wizards of
 * the coast" must not ask the catalogue for a card whose name contains "of".
 */
const STOPWORDS = new Set(['of', 'the', 'and', 'era']);

/** Strip punctuation and the joining words, so two spellings collapse to one. */
const flatten = (s) => String(s || '')
  .toLowerCase()
  .replace(/&/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\b(and|the|of|era)\b/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * Every way a card number might be written, given one run of digits.
 * The catalogue stores "4" where a person types "004", and "066" where a
 * person types "66", so both directions have to be covered.
 */
export function numberForms(digits) {
  const bare = String(digits).replace(/^0+/, '') || '0';
  const out = new Set([String(digits), bare]);
  for (const width of [2, 3, 4]) {
    if (bare.length < width) out.add(bare.padStart(width, '0'));
  }
  return [...out];
}

/**
 * Split a raw query into the parts that go to different fields.
 *
 * Every token is cut into its letter runs and its digit runs, so "char004" and
 * "char 004" parse identically and "swsh066" keeps both the prefix and the
 * number. Order is never significant: "004 char" is the same query as
 * "char 004".
 *
 * @returns {{
 *   raw: string, flat: string, words: string[], numbers: string[],
 *   codes: string[], localIds: string[], isEmpty: boolean
 * }}
 */
/**
 * The words people say for a printing or a finish, and what the catalogue
 * calls it. "Mewtwo 10 shadowless" is a name, a number and a printing; taken
 * as three name words it finds nothing at all, because no card is called
 * "shadowless".
 *
 * Matched against the labels printings.js builds, so a term here has to be a
 * label there. The value is what gets compared, lowercased.
 */
const PRINTING_TERMS = {
  shadowless: 'shadowless',
  unlimited: 'unlimited',
  '1st': '1st edition',
  '1sted': '1st edition',
  '1stedition': '1st edition',
  first: '1st edition',
  firsted: '1st edition',
  firstedition: '1st edition',
  '4th': '4th print',
  '4thprint': '4th print',
  fourth: '4th print',
  holo: 'holo',
  holofoil: 'holo',
  foil: 'holo',
  reverse: 'reverse holo',
  reverseholo: 'reverse holo',
  rh: 'reverse holo',
  normal: 'standard',
  standard: 'standard',
  cosmos: 'cosmos',
  galaxy: 'galaxy',
  mirror: 'mirror',
  promo: 'promo',
  masterball: 'master ball',
  pokeball: 'poke ball',
  staff: 'staff stamp',
  prerelease: 'prerelease stamp',
};

/** Two words that only mean a printing together: "1st ed", "reverse holo". */
const PRINTING_PAIRS = {
  '1st edition': '1st edition',
  '1st ed': '1st edition',
  'first edition': '1st edition',
  'first ed': '1st edition',
  '4th print': '4th print',
  'reverse holo': 'reverse holo',
  'master ball': 'master ball',
  'poke ball': 'poke ball',
};

/** What a query asked for, printing-wise, as catalogue labels in lower case. */
export function printingsIn(text) {
  let lower = String(text || '').toLowerCase();
  const found = new Set();
  // Pairs first, and taken out of the string: "reverse holo" is one printing,
  // not reverse plus holo.
  for (const [pair, label] of Object.entries(PRINTING_PAIRS)) {
    if (lower.includes(pair)) { found.add(label); lower = lower.split(pair).join(' '); }
  }
  for (const token of lower.split(/[\s,./]+/).filter(Boolean)) {
    const hit = PRINTING_TERMS[token];
    if (hit) found.add(hit);
  }
  return [...found];
}

/** Is this whole token only a way of saying a printing? */
const isPrintingToken = (token) => Boolean(PRINTING_TERMS[token]);

export function parseQuery(raw) {
  const text = String(raw || '').trim();
  const lower = text.toLowerCase();
  const tokens = lower.split(/[\s,./]+/).filter(Boolean);

  const words = [];
  const numbers = [];
  const codes = [];
  const setCodes = [];
  const localIds = new Set();

  for (const token of tokens) {
    // A whole card id, "sv4-125" or "base1-4". The part before the dash is a
    // set, not a name: asking the catalogue for a card called "sv" finds
    // nothing, and the number alone finds 213 of them.
    const asCardId = token.match(/^([a-z]{1,6}\d*[a-z]?)-([a-z]*\d+[a-z]*)$/);
    if (asCardId) {
      setCodes.push(asCardId[1]);
      localIds.add(asCardId[2].toUpperCase());
      const digits = asCardId[2].match(/\d+/);
      if (digits) for (const form of numberForms(digits[0])) localIds.add(form);
      continue;
    }

    // "1st" and "4th" are printings, and the 1 and the 4 in them are not card
    // numbers. The whole token goes.
    if (isPrintingToken(token)) continue;

    const letters = token.match(/[a-z]+/g) || [];
    const digits = token.match(/\d+/g) || [];
    if (letters.length && digits.length) codes.push(token);
    for (const run of letters) {
      // A printing is not part of the card's name. Left in, "shadowless" asks
      // the catalogue for a card by that name and the search comes back empty.
      if (run.length >= 2 && !STOPWORDS.has(run) && !PRINTING_TERMS[run]) words.push(run);
    }
    for (const run of digits) numbers.push(run);
  }

  // Candidate local_id values, uppercased the way the catalogue stores them.
  // A prefixed code ("swsh066") contributes its own padded forms too, because
  // the catalogue writes SWSH066 and a person may type swsh66.
  for (const n of numbers) for (const form of numberForms(n)) localIds.add(form);
  for (const code of codes) {
    const m = code.match(/^([a-z]+)(\d+)$/);
    localIds.add(code.toUpperCase());
    if (m) for (const form of numberForms(m[2])) localIds.add(`${m[1].toUpperCase()}${form}`);
  }

  const printings = printingsIn(text);

  return {
    raw: text,
    flat: flatten(text),
    printings,
    words: [...new Set(words)],
    numbers: [...new Set(numbers)],
    codes,
    setCodes,
    localIds: [...localIds],
    isEmpty: !words.length && !numbers.length && !localIds.size,
  };
}

/**
 * Is this query worth sending anywhere? One digit is a real search ("4" is a
 * card number); one letter is not.
 */
export function isSearchable(parsed) {
  if (!parsed || parsed.isEmpty) return false;
  if (parsed.numbers.length || parsed.localIds.length) return true;
  return parsed.words.some((w) => w.length >= 2);
}

// ---------------------------------------------------------------------------
// Matching against lists already in memory. 13 eras and 213 sets, so this is
// free and never needs a round trip.
// ---------------------------------------------------------------------------

/** Do any of the words in `haystack` open with at least four of `word`'s letters? */
function sharesPrefix(word, haystack, min = 4) {
  if (word.length < min) return false;
  for (const part of haystack.split(' ')) {
    if (part.length < min) continue;
    let i = 0;
    while (i < word.length && i < part.length && word[i] === part[i]) i += 1;
    if (i >= min) return true;
  }
  return false;
}

/**
 * Eras that answer this query, best first.
 * Accepts the raw `digitalcards.eras` row shape: { id, display_name, ... }.
 */
export function matchEras(parsed, eras = []) {
  if (!isSearchable(parsed)) return [];
  const flat = parsed.flat;
  const scored = [];

  for (const era of eras) {
    if (!era || !era.id) continue;
    const id = String(era.id).toLowerCase();
    const name = flatten(era.display_name || era.id);
    let score = 0;

    if (parsed.words.includes(id) || flat === flatten(id)) score = 100;

    if (!score) {
      for (const phrase of ERA_SYNONYMS[era.id] || []) {
        const p = flatten(phrase);
        if (!p) continue;
        // Either the person typed the phrase, or the phrase is what they were
        // reaching for. "wizards" hits "wizards of the coast" both ways.
        if (flat === p) { score = Math.max(score, 95); continue; }
        if (flat.includes(p) || p.includes(flat)) score = Math.max(score, 88);
      }
    }

    // Falling back on the era's own display name, which is how "diamond"
    // reaches dp even though the synonym row never anticipated that word.
    if (!score && parsed.words.length) {
      const hits = parsed.words.filter((w) => name.includes(w)).length;
      if (hits === parsed.words.length) score = 70;
      else if (hits) score = 40;
    }

    if (score) scored.push({ ...era, _score: score, _kind: 'era' });
  }

  return scored.sort((a, b) => b._score - a._score);
}

/**
 * Sets that answer this query, best first.
 *
 * Takes either the raw `digitalcards.sets` row or the legacy adapter shape the
 * Collection screen carries, so a caller never has to convert first.
 */
export function matchSets(parsed, sets = []) {
  if (!isSearchable(parsed)) return [];
  const scored = [];

  for (const set of sets) {
    if (!set || !set.id) continue;
    const id = String(set.id).toLowerCase();
    const name = flatten(set.name);
    const abbr = String(set.abbreviation || '').toLowerCase();
    let score = 0;

    if (id === parsed.flat || (abbr && abbr === parsed.flat)) score = 100;
    else if (parsed.setCodes.includes(id) || parsed.codes.includes(id)) score = 100;
    else if (name && name.startsWith(parsed.flat)) score = 92;
    else if (parsed.words.length) {
      const hits = parsed.words.filter((w) => name.includes(w)).length;
      if (hits === parsed.words.length) score = 75;
      // "evolv" is not inside "evolutions" — the fifth letter diverges — but it
      // is obviously reaching for it. A shared opening of four or more letters
      // against any word in the set name catches the near miss without opening
      // the door to unrelated matches.
      else if (parsed.words.every((w) => sharesPrefix(w, name))) score = 60;
    }

    if (score) scored.push({ set, score });
  }

  // Oldest first inside a score band, matching how sets read everywhere else.
  return scored
    .sort((a, b) => (b.score - a.score)
      || String(a.set.release_date || a.set.releaseDate || '').localeCompare(String(b.set.release_date || b.set.releaseDate || '')))
    .map(({ set, score }) => ({ ...set, _score: score, _kind: 'set' }));
}

/**
 * The words in a query that a matched set has already explained.
 *
 * "evolv char" should not ask the catalogue for a card named both "evolv" and
 * "char". The set eats "evolv", and "char" is what is left to look for inside
 * it.
 */
export function residualWords(parsed, setHits = []) {
  if (!setHits.length) return parsed.words;
  const names = setHits.map((s) => flatten(s.name));
  return parsed.words.filter((w) => !names.some((n) => n.includes(w)));
}

/**
 * Reorder card rows so the obvious answer is first.
 *
 * The server returns them alphabetically, which puts "Dark Charizard" above
 * plain "Charizard". A person typing "char" means the Charizard.
 */
export function rankCards(parsed, rows = []) {
  const first = parsed.words[0] || '';
  const weight = (row) => {
    const name = String(row.name || '').toLowerCase();
    if (!first) return 2;
    if (name === first) return 0;
    if (name.startsWith(first)) return 1;
    return 2;
  };
  return [...rows].sort((a, b) => weight(a) - weight(b)
    || String(a.name || '').localeCompare(String(b.name || '')));
}

const smartSearch = {
  ERA_SYNONYMS, parseQuery, numberForms, isSearchable,
  matchEras, matchSets, residualWords, rankCards,
};
export default smartSearch;
